import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { DIRECTORY_PORTAL_URL } from '@services';
import { MatIconModule } from '@angular/material/icon';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HomeHeaderComponent } from '../../shared/_components/home/header/header.component';
import { HomeFooterComponent } from '../../shared/_components/home/footer/footer.component';
import { Dialog } from '@angular/cdk/dialog';
import { VideoDialogComponent } from '../../shared/_components/home/video-dialog/video-dialog.component';
import { Title, Meta } from '@angular/platform-browser';

@Component({
  selector: 'directory-portal-home',
  imports: [MatIconModule, HomeHeaderComponent, HomeFooterComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly dialog = inject(Dialog);

  private readonly directoryPortalUrl: string = inject(DIRECTORY_PORTAL_URL);

  // SEO services & helpers (injected)
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);

  features: {
    title: string;
    description: string;
    image: string;
  }[] = [
    {
      title: $localize`:@@home.features.title-1:Evidence-Based Protocols`,
      description: $localize`:@@home.features.description-1:Access ready-to-use nutritional, herbal, and lifestyle treatment protocols for chronic conditions.`,
      image: 'education',
    },
    {
      title: $localize`:@@home.features.title-4:SmartForm + AI Insights`,
      description: $localize`:@@home.features.description-4:Instantly analyze patient data and uncover clinical patterns with powerful AI.`,
      image: 'file-chart-bar',
    },
    {
      title: $localize`:@@home.features.title-2:Integrated Scheduler`,
      description: $localize`:@@home.features.description-2:Sync with Google & Outlook to manage your calendar and bookings seamlessly.`,
      image: 'calendar-month',
    },
    {
      title: $localize`:@@home.features.title-5:Virtual Sessions`,
      description: $localize`:@@home.features.description-5:Secure telehealth built right in for easy patient follow-up.`,
      image: 'file-chart-bar',
    },
    {
      title: $localize`:@@home.features.title-3:Patient Charts`,
      description: $localize`:@@home.features.description-3:Centralized records, treatment plans, and progress tracking in one place.`,
      image: 'user-circle',
    },
    {
      title: $localize`:@@home.features.title-6:Grow Your Practice`,
      description: $localize`:@@home.features.description-6:Post your clinic in the iCareDx Directory and attract new clients searching for Functional Medicine providers.`,
      image: 'chart-mixed-dollar',
    },
  ];

  constructor() {
    afterNextRender(() => this.loadWistiaScripts());
  }

  ngOnInit(): void {
    this.applySEO();
  }

  onLearnMoreClick(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.dialog.open(VideoDialogComponent);
    }
  }

  private loadWistiaScripts(): void {
    this.appendScript('https://fast.wistia.com/player.js');
    this.appendScript('https://fast.wistia.com/embed/fl4hntm6lf.js', 'module');
  }

  private appendScript(src: string, type?: string): void {
    if (this.document.querySelector(`script[src="${src}"]`)) {
      return;
    }

    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'src', src);
    this.renderer.setAttribute(script, 'async', '');
    if (type) {
      this.renderer.setAttribute(script, 'type', type);
    }
    this.renderer.appendChild(this.document.head, script);
  }

  applySEO(): void {
    const siteUrl = this.directoryPortalUrl;
    const pageUrl = `${siteUrl}${this.document.location?.pathname || '/'}`;
    const pageTitle = 'iCareDx Directory — Find Functional Medicine Providers';
    const pageDescription =
      'Discover vetted Functional Medicine providers, book appointments, and access integrative care resources on the iCareDx Directory.';
    const siteName = 'iCareDx';
    const logoUrl = `${siteUrl}/assets/directory-portal/logo.png`;
    const imageUrl = `${siteUrl}/assets/directory-portal/logo.png`;

    this.titleService.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: pageDescription });

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({
      property: 'og:description',
      content: pageDescription,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });
    this.meta.updateTag({ property: 'og:site_name', content: siteName });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });

    // Twitter
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({
      name: 'twitter:description',
      content: pageDescription,
    });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    // Canonical link (create or update)
    let canonical = this.document.querySelector(
      "link[rel='canonical']"
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = this.renderer.createElement('link') as HTMLLinkElement;
      this.renderer.setAttribute(canonical, 'rel', 'canonical');
      this.renderer.appendChild(this.document.head, canonical);
    }
    this.renderer.setAttribute(canonical, 'href', pageUrl);

    // JSON-LD structured data
    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${siteUrl}#organization`,
          name: siteName,
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: logoUrl,
          },
        },
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}#website`,
          url: siteUrl,
          name: siteName,
          publisher: { '@id': `${siteUrl}#organization` },
        },
        {
          '@type': 'WebPage',
          '@id': `${pageUrl}#webpage`,
          url: pageUrl,
          name: pageTitle,
          description: pageDescription,
          isPartOf: { '@id': `${siteUrl}#website` },
        },
      ],
    };

    // Insert/replace JSON-LD <script> in head
    const existingJsonLd = this.document.querySelector(
      "script[type='application/ld+json']"
    );
    const script = existingJsonLd || this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'application/ld+json');

    // Avoid using innerHTML on server; use textContent in browser
    const jsonText = JSON.stringify(jsonLd, null, 2);
    if ((script as HTMLScriptElement).textContent !== undefined) {
      (script as HTMLScriptElement).textContent = jsonText;
    } else {
      // fallback for environments where textContent isn't writable
      script.innerHTML = jsonText;
    }

    if (!existingJsonLd) {
      this.renderer.appendChild(this.document.head, script);
    }
  }
}
